// ============================================================================
// ADR-0024 — SECTIONS narrative helpers (parse + variant resolution)
// ============================================================================
// Shared by the Board's Report editor (SectionedReportPane) and the
// Corporate Facts applicability questionnaire, so both read one definition
// of "which questions exist" and "which variant is in force".

import type {
  ChoiceOption,
  QuestionScope,
  ReportLiveData,
  ReportSectionDefinition,
  ReportSectionOverride,
  ReportTable,
  ReportWorkspaceData,
  SectionInput,
} from 'api/types/fsa/reportData.types';
import type { CorporateFactsData } from 'api/types/fsa/corporateFacts.types';

/** 'default' | 'applies' | 'not_applicable', or — for multiple choice — the option value in force. */
export type VariantName = string;
export type VariantSource = 'always' | 'override' | 'facts' | 'derived' | 'default';

export interface ResolvedVariant {
  variant: VariantName;
  source: VariantSource;
  content: unknown | null;
  /** False when the section resolves "not applicable" (yes/no No, or a choice option flagged not_applicable). */
  applies: boolean;
  /** Human label of the answer in force (option label, "Applies" / "Not applicable"). */
  label: string;
  /** Title for the answer in force (variant_titles), else null. */
  title: string | null;
}

export interface ApplicabilityQuestion {
  code: string;
  prompt: string;
  defaultAnswer: boolean | string | null;
  /** Corporate Facts list whose entries answer this question when unanswered. */
  deriveFrom: string | null;
  /** Titles of the sections the answer drives. */
  sectionTitles: string[];
  /** ADR-0024 A-1 — "audit" / N-2 "note" = answered in the editor's question panel; null = Corporate Facts. */
  scope: QuestionScope;
  /** ADR-0024 A-1 — multiple-choice options; null = yes/no. */
  options: ChoiceOption[] | null;
}

/** The facts map a question's answer lives in (ADR-0024 A-1). */
export function answersFor(
  scope: QuestionScope | undefined,
  facts: CorporateFactsData | undefined,
): Record<string, boolean | string> | undefined {
  return scope === 'audit' ? facts?.audit_answers
    : scope === 'note' ? facts?.note_answers
      : facts?.applicability;
}

// ---------------------------------------------------------------------------
// ADR-0024 N-2 — section inputs (figures / text typed in the question panel)
// ---------------------------------------------------------------------------

/** Key of a field's rounded form (Rounded amount mode) — mirrors the server's ReportRounding. */
export const ROUNDED_SUFFIX = '@rounded';

/** The engagement's rounding factor for generated notes: divisor + unit word (mirrors ReportRounding.From). */
export interface NoteRounding {
  divisor: number;
  unit: string;
}

export function noteRounding(factorKey: string | null | undefined, divisor: number | null | undefined): NoteRounding | null {
  if (!divisor || divisor <= 1) return null;
  const unit: Record<string, string> = {
    HUNDREDS: 'hundreds', THOUSANDS: 'thousands', LAKHS: 'lakhs', CRORES: 'crores', MILLIONS: 'millions', BILLIONS: 'billions',
  };
  return { divisor, unit: unit[(factorKey ?? '').toUpperCase()] ?? (factorKey ?? '').toLowerCase() };
}

/** Fields / tables as shown in an amount mode: Rounded takes each value's rounded form where there is one. */
export function inAmountMode(
  fields: Record<string, string>,
  tables: Record<string, ReportTable>,
  mode: 'actual' | 'rounded',
): { fields: Record<string, string>; tables: Record<string, ReportTable> } {
  if (mode !== 'rounded') return { fields, tables };
  const f = { ...fields };
  for (const [k, v] of Object.entries(fields)) if (k.endsWith(ROUNDED_SUFFIX)) f[k.slice(0, -ROUNDED_SUFFIX.length)] = v;
  const t: Record<string, ReportTable> = {};
  for (const [k, v] of Object.entries(tables)) t[k] = v.roundedRows ? { ...v, rows: v.roundedRows } : v;
  return { fields: f, tables: t };
}

/** Field paths resolved from a note's inputs — always live, never snapshotted. */
export function isInputPath(path: string): boolean {
  return path.startsWith('inputs.');
}

/** Statement format, mirroring the server's ReportValueFormat.Amount: Indian grouping, 2 decimals, negatives in parentheses. */
export function formatStatementAmount(value: number): string {
  const abs = Math.abs(Math.round(value * 100) / 100);
  const [intPart, frac] = abs.toFixed(2).split('.');
  let grouped = intPart;
  if (intPart.length > 3) {
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    grouped = `${rest},${last3}`;
  }
  const s = `${grouped}.${frac}`;
  return value < 0 ? `(${s})` : s;
}

/** Where an input is stored: this year's `note_inputs`, or `note_standing` (carried). */
export function inputScope(input: Pick<SectionInput, 'scope'>): 'inputs' | 'standing' {
  return input.scope === 'standing' ? 'standing' : 'inputs';
}

/** An input's current value, from the store its scope names. */
export function inputValueOf(input: Pick<SectionInput, 'code' | 'scope'>, facts: CorporateFactsData | undefined): number | string | undefined {
  return inputScope(input) === 'standing' ? facts?.note_standing?.[input.code] : facts?.note_inputs?.[input.code];
}

/** The inputs' current values as field values (inputs.<code>), from Corporate Facts (standing first; this year's input of the same code wins, as on the server). */
export function inputFieldValues(facts: CorporateFactsData | undefined, rounding?: NoteRounding | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [code, v] of [...Object.entries(facts?.note_standing ?? {}), ...Object.entries(facts?.note_inputs ?? {})]) {
    if (typeof v === 'number') {
      out[`inputs.${code}`] = formatStatementAmount(v);
      if (rounding) {
        const r = Math.round((v / rounding.divisor) * 100) / 100;
        out[`inputs.${code}${ROUNDED_SUFFIX}`] = `${formatStatementAmount(r)}${rounding.unit ? ` ${rounding.unit}` : ''}`;
      }
    }
    else if (typeof v === 'string' && v.trim()) out[`inputs.${code}`] = v.trim();
  }
  return out;
}

export interface VisibleInput extends SectionInput {
  sectionTitle: string;
}

/** Inputs of the sections whose variant in force shows them, in section order. */
export function collectInputs(
  defs: ReportSectionDefinition[],
  variantOf: (code: string) => string | undefined,
): VisibleInput[] {
  const out: VisibleInput[] = [];
  const seen = new Set<string>();
  for (const d of defs) {
    for (const input of d.inputs ?? []) {
      if (seen.has(input.code)) continue;
      const v = variantOf(d.code);
      if (input.show_when && input.show_when.length > 0 && (!v || !input.show_when.includes(v))) continue;
      seen.add(input.code);
      out.push({ ...input, sectionTitle: d.title });
    }
  }
  return out;
}

/** A recorded answer valid for the question (boolean for yes/no, a listed option for choice), else undefined. */
export function answerOf(q: Pick<ApplicabilityQuestion, 'code' | 'scope' | 'options'>, facts: CorporateFactsData | undefined): boolean | string | undefined {
  const v = answersFor(q.scope, facts)?.[q.code];
  if (q.options) return typeof v === 'string' && q.options.some(o => o.value === v) ? v : undefined;
  return typeof v === 'boolean' ? v : undefined;
}

export function parseSectionDefinitions(json: string | null | undefined): ReportSectionDefinition[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr)
      ? arr.filter((s: any) => s && typeof s === 'object' && typeof s.code === 'string')
      : [];
  } catch {
    return [];
  }
}

/** True when the Corporate Facts list a question derives from has entries. */
export function derivedFromFacts(
  deriveFrom: string | null | undefined,
  facts: Record<string, unknown> | undefined,
): boolean {
  if (!deriveFrom || !facts) return false;
  const list = facts[deriveFrom];
  return Array.isArray(list) && list.length > 0;
}

/**
 * Variant in force for one section. Precedence (mirrored server-side by
 * SectionedNarrativeResolver): the auditor's per-section override → the
 * Corporate Facts answer → "applies" when the question's derive_from list has
 * entries → the section's default answer. A section with no applicability
 * question always uses its `default` variant.
 */
export function resolveVariant(
  def: ReportSectionDefinition,
  override: ReportSectionOverride | undefined,
  facts: CorporateFactsData | undefined,
): ResolvedVariant {
  const a = def.applicability;
  const q = a?.question;
  if (!a || !q) {
    return { variant: 'default', source: 'always', content: def.variants?.default ?? null, applies: true, label: '', title: null };
  }

  // ADR-0024 A-1 — multiple choice: override → answer → default_answer → first option.
  if (a.options && a.options.length > 0) {
    const valid = (v: unknown) => (typeof v === 'string' && a.options!.some(o => o.value === v) ? v : undefined);
    let key: string;
    let source: VariantSource;
    const forced = valid(override?.variant_override);
    const answered = valid(answersFor(a.scope, facts)?.[q]);
    if (forced) { key = forced; source = 'override'; }
    else if (answered) { key = answered; source = 'facts'; }
    else { key = valid(a.default_answer) ?? a.options[0].value; source = 'default'; }
    const option = a.options.find(o => o.value === key)!;
    return {
      variant: key,
      source,
      content: def.variants?.[key] ?? def.variants?.default ?? null,
      applies: option.not_applicable !== true,
      label: option.label,
      title: def.variant_titles?.[key] ?? null,
    };
  }

  const answer = answersFor(a.scope, facts)?.[q];
  let applies: boolean;
  let source: VariantSource;
  if (override?.variant_override === 'applies' || override?.variant_override === 'not_applicable') {
    applies = override.variant_override === 'applies';
    source = 'override';
  } else if (typeof answer === 'boolean') {
    applies = answer;
    source = 'facts';
  } else if (derivedFromFacts(a.derive_from, facts)) {
    applies = true;
    source = 'derived';
  } else {
    applies = a.default_answer === true;
    source = 'default';
  }
  const variant: VariantName = applies ? 'applies' : 'not_applicable';
  return {
    variant,
    source,
    content: def.variants?.[variant] ?? null,
    applies,
    label: applies ? 'Applies' : 'Not applicable',
    title: def.variant_titles?.[variant] ?? null,
  };
}

// ---------------------------------------------------------------------------
// ADR-0024 §5 — data snapshot (saved-as-primary)
// ---------------------------------------------------------------------------

/**
 * Fill snapshot keys that do not exist yet from the live values — first open,
 * or a field / table added since the last save. Existing snapshot values are
 * never touched: once saved, a value changes only through Refresh.
 */
export function seedResolvedData(ws: ReportWorkspaceData, live: ReportLiveData | null | undefined): ReportWorkspaceData {
  if (!live) return ws;
  const fields = { ...(ws.resolved_fields ?? {}) };
  for (const [k, v] of Object.entries(live.fields ?? {})) if (!(k in fields) && !isInputPath(k)) fields[k] = v;
  const tables = { ...(ws.resolved_tables ?? {}) };
  for (const [k, v] of Object.entries(live.tables ?? {})) if (!(k in tables)) tables[k] = v;
  return { ...ws, resolved_fields: fields, resolved_tables: tables };
}

export interface DriftItem {
  kind: 'field' | 'table';
  key: string;
  label: string;
  saved?: string;
  live?: string;
}

/**
 * The field paths and table sources the document actually prints: fields and
 * tables inside the text of every section that prints (not hidden, not left
 * out by its answer), as it prints — the section's own text, else the wording
 * in force. The snapshot holds every value the engagement can resolve; only
 * these matter to this document.
 */
export function usedDataKeys(
  defs: ReportSectionDefinition[],
  ws: ReportWorkspaceData,
  facts: CorporateFactsData | undefined,
): { fields: Set<string>; tables: Set<string> } {
  const fields = new Set<string>();
  const tables = new Set<string>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const n = node as { type?: string; attrs?: { path?: string; source?: string }; content?: unknown[] };
    if (n.type === 'fieldChip' && n.attrs?.path) fields.add(n.attrs.path);
    if (n.type === 'dataTable' && n.attrs?.source) tables.add(n.attrs.source);
    (n.content ?? []).forEach(walk);
  };
  const data = ws.section_data ?? {};
  const direct = ws.authoring_mode === 'DIRECT';
  for (const d of defs) {
    const ov = data[d.code] ?? {};
    if (ov.is_hidden) continue;
    const r = resolveVariant(d, ov, facts);
    if (!direct && isOmitted(d, r)) continue;
    walk(ov.content ?? r.content);
  }
  for (const a of ws.added_sections ?? []) {
    const ov = data[a.local_id] ?? {};
    if (!ov.is_hidden) walk(ov.content ?? a.content);
  }
  return { fields, tables };
}

/**
 * Snapshot entries whose live value now differs (a value that became
 * unresolvable is not drift). With `used`, only the values the document
 * prints count — a change to a table this document never shows (e.g. the
 * directors in an Accounting Policies note) is not drift here.
 */
export function computeDrift(
  ws: ReportWorkspaceData,
  live: ReportLiveData | null | undefined,
  used?: { fields: Set<string>; tables: Set<string> },
): DriftItem[] {
  if (!live) return [];
  const labels = new Map((live.catalog ?? []).map(c => [c.path, c.label]));
  const out: DriftItem[] = [];
  for (const [k, liveValue] of Object.entries(live.fields ?? {})) {
    if (isInputPath(k) || k.endsWith(ROUNDED_SUFFIX)) continue;
    if (used && !used.fields.has(k)) continue;
    const saved = ws.resolved_fields?.[k];
    if (saved !== undefined && saved !== liveValue) {
      out.push({ kind: 'field', key: k, label: labels.get(k) ?? k, saved, live: liveValue });
    }
  }
  for (const [k, liveTable] of Object.entries(live.tables ?? {})) {
    if (used && !used.tables.has(k)) continue;
    const saved = ws.resolved_tables?.[k];
    if (saved !== undefined && !tablesEqual(saved, liveTable)) {
      out.push({ kind: 'table', key: k, label: TABLE_LABELS[k] ?? k });
    }
  }
  return out;
}

/**
 * Content equality for snapshotted tables. NOT JSON.stringify: the saved
 * snapshot round-trips through a PostgreSQL jsonb column, which re-orders
 * object keys (shorter keys first), so an unchanged table serialises
 * differently from the live one and every table would read as "changed".
 * A missing and a null emptyNote are the same.
 */
export function tablesEqual(a: ReportTable | undefined, b: ReportTable | undefined): boolean {
  if (!a || !b) return a === b;
  const sameList = (x: string[] | undefined, y: string[] | undefined) => {
    const xs = x ?? [];
    const ys = y ?? [];
    return xs.length === ys.length && xs.every((v, i) => v === ys[i]);
  };
  const rowsA = a.rows ?? [];
  const rowsB = b.rows ?? [];
  return sameList(a.columns, b.columns)
    && sameList(a.align, b.align)
    && (a.emptyNote ?? null) === (b.emptyNote ?? null)
    && (a.emptyText ?? null) === (b.emptyText ?? null)
    && sameList((a.boldRows ?? []).map(String), (b.boldRows ?? []).map(String))
    && (a.roundedRows ?? []).length === (b.roundedRows ?? []).length
    && (a.roundedRows ?? []).every((r, i) => sameList(r, (b.roundedRows ?? [])[i]))
    && rowsA.length === rowsB.length
    && rowsA.every((r, i) => sameList(r, rowsB[i]));
}

/** Explicit Refresh — overwrite the snapshot with the live values. */
export function refreshResolvedData(ws: ReportWorkspaceData, live: ReportLiveData): ReportWorkspaceData {
  return {
    ...ws,
    resolved_fields: Object.fromEntries(
      Object.entries({ ...(ws.resolved_fields ?? {}), ...(live.fields ?? {}) }).filter(([k]) => !isInputPath(k)),
    ),
    resolved_tables: { ...(ws.resolved_tables ?? {}), ...(live.tables ?? {}) },
  };
}

/** Human labels for the data-table sources the resolver builds. */
export const TABLE_LABELS: Record<string, string> = {
  financial_summary: 'Financial summary',
  directors: 'Directors',
  attendance: 'Attendance at Board meetings',
  aoc2_non_arms_length: "AOC-2 — not at arm's length",
  aoc2_arms_length: "AOC-2 — at arm's length",
  depreciation_rates: 'Depreciation rates (Depreciation Calc)',
  'grid.RATIOS': 'Analytical ratios',
  amortisation_rates: 'Amortisation rates (Depreciation Calc)',
};

/** omit_when_not_applicable sections (e.g. AOC-2) do not print when their question resolves "not applicable". */
export function isOmitted(def: ReportSectionDefinition, resolved: ResolvedVariant): boolean {
  return def.omit_when_not_applicable === true && !resolved.applies;
}

/** Distinct applicability questions, in section order (first prompt wins). */
export function collectQuestions(defs: ReportSectionDefinition[]): ApplicabilityQuestion[] {
  const byCode = new Map<string, ApplicabilityQuestion>();
  for (const d of defs) {
    const a = d.applicability;
    if (!a?.question) continue;
    const existing = byCode.get(a.question);
    if (existing) {
      existing.sectionTitles.push(d.title);
      existing.deriveFrom = existing.deriveFrom ?? a.derive_from ?? null;
    } else {
      byCode.set(a.question, {
        code: a.question,
        prompt: a.prompt,
        defaultAnswer: typeof a.default_answer === 'boolean' || typeof a.default_answer === 'string' ? a.default_answer : null,
        deriveFrom: a.derive_from ?? null,
        sectionTitles: [d.title],
        scope: a.scope === 'audit' || a.scope === 'note' ? a.scope : null,
        options: a.options && a.options.length > 0 ? a.options : null,
      });
    }
  }
  return Array.from(byCode.values());
}
